// Identity-filterable widget (TASK-495). Adapts ClientLevelView's filter-keyed data
// path (useClientLevelData) into a clean canvas tile WITHOUT the routed page's
// SecondaryNav / tabs / AI chat (which read poorly in a tile) — the route
// ClientLevelView is left untouched. clientId is server-injected (TASK-499); only
// dateMode + benchmark genuinely narrow server-side (useClientLevelData forwards just
// those to getClientDashboardData), which is exactly the ClientLevelView filter row.
import { useMemo, useState } from 'react';
import { useCanvasStore } from '../../../stores/canvasStore';
import { useClientLevelData } from '../../../hooks/useClientLevelData';
import { getDefaultClientFilters } from '../../../types/portfolio-views';
import PortfolioBreakdown from '../../portfolio/PortfolioBreakdown';
import ClientInsightsPanel from '../../portfolio/ClientInsightsPanel';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetFilterBar } from '../WidgetFilterBar';

type IdentityFilters = {
  dateMode?: 'latest' | 'historical';
  benchmark?: string;
  [key: string]: unknown;
};

export function ClientLevelWidget({ clientId, filters }: { clientId: string; filters?: IdentityFilters }) {
  const addSpecs = useCanvasStore((s) => s.addSpecs);
  const [active, setActive] = useState<IdentityFilters>(filters ?? {});

  // dateMode drives the API period inside the filter-keyed hook; benchmark rides
  // alongside. Memoised so the query key is stable across unrelated re-renders.
  const clientFilters = useMemo(
    () => ({ ...getDefaultClientFilters(), dateMode: active.dateMode ?? 'latest' }),
    [active.dateMode],
  );
  const benchmark = active.benchmark ?? 'SPI';

  const { data, loading, error } = useClientLevelData(clientId, clientFilters, benchmark);

  // On the canvas a portfolio click summons the portfolio tile instead of leaving the
  // grid (TASK-500 Phase-2). ClientLevelWidget is canvas-only (routed page is
  // ClientLevelView), so the sink is always live. ponytail: portfolio-specific
  // selection is a refinement — CockpitPortfolioView is keyed on clientId.
  const handlePortfolioClick = (_portfolioId: string) =>
    addSpecs([{ component: 'CockpitPortfolioView', props: { clientId } }]);

  return (
    <WidgetContainer
      title="Client Portfolio"
      source="Portfolio API"
      toolbar={
        <WidgetFilterBar
          component="ClientLevelView"
          values={active}
          onChange={(partial) => setActive((prev) => ({ ...prev, ...partial }))}
        />
      }
    >
      {loading && <p className="py-8 text-center text-sm text-muted-foreground">Loading client…</p>}
      {error && <p className="py-8 text-center text-sm text-destructive">{error}</p>}
      {data && (
        <div className="space-y-4">
          <PortfolioBreakdown portfolios={data.portfolios} onPortfolioClick={handlePortfolioClick} />
          <ClientInsightsPanel insights={data.insights} />
        </div>
      )}
    </WidgetContainer>
  );
}
